import React, { useState } from 'react';
import { EmailIcon, PhoneIcon, BuildingIcon, CheckCircleIcon } from '../common/icons';
import './ContactForm.css';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  interest: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        interest: ''
      });
    }, 3000);
  };

  if (isSubmitted) {
    return (
      <section className="contact-form-section">
        <div className="contact-form-container">
          <div className="contact-success">
            <CheckCircleIcon width={48} height={48} className="success-icon" />
            <h2 className="success-title">Thank You!</h2>
            <p className="success-message">
              We've received your message and will get back to you within 24 hours.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="contact-form-section" id="contact">
      <div className="contact-form-container">
        <div className="contact-form-content">
          <div className="contact-form-header">
            <h2 className="contact-form-title">Get Started Today</h2>
            <p className="contact-form-subtitle">
              Ready to transform your supply chain? Let's discuss how Optinode AI 
              can optimize your warehouse operations and reduce costs.
            </p>
          </div>

          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="john.smith@company.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your Company Inc."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="interest" className="form-label">
                    Primary Interest
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Select an option</option>
                    <option value="warehouse-optimization">Warehouse Optimization</option>
                    <option value="inventory-management">Inventory Management</option>
                    <option value="demand-forecasting">Demand Forecasting</option>
                    <option value="sop-planning">S&OP Planning</option>
                    <option value="ai-insights">AI-Powered Insights</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Tell us about your challenges
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="form-textarea"
                  placeholder="Describe your current supply chain challenges and how we can help..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`form-submit ${isSubmitting ? 'submitting' : ''}`}
                >
                  {isSubmitting ? (
                    <>
                      <span className="submit-spinner"></span>
                      Sending Message...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                <p className="form-privacy">
                  By submitting this form, you agree to our{' '}
                  <a href="#privacy" className="privacy-link">Privacy Policy</a>
                </p>
              </div>
            </form>

            <div className="contact-info">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <EmailIcon width={24} height={24} color="#007AFF" />
                </div>
                <div className="contact-info-content">
                  <h4 className="contact-info-title">Email Us</h4>
                  <p className="contact-info-text">hello@optinode.ai</p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <PhoneIcon width={24} height={24} color="#007AFF" />
                </div>
                <div className="contact-info-content">
                  <h4 className="contact-info-title">Call Us</h4>
                  <p className="contact-info-text">+1 (555) 123-4567</p>
                </div>
              </div>
              
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <BuildingIcon width={24} height={24} color="#007AFF" />
                </div>
                <div className="contact-info-content">
                  <h4 className="contact-info-title">Visit Us</h4>
                  <p className="contact-info-text">
                    123 Innovation Drive<br />
                    San Francisco, CA 94105
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;